"use client";

import { useEffect } from "react";

// Cookie format upgraded 2026-04-30 (V1.5):
//   id:seenCount:lastSeenTimestamp|id:seenCount:lastSeenTimestamp|…
//
// We still READ the legacy `id1,id2,id3` format when older browsers come back
// with it (PR #453 visitors) so their pin state survives the upgrade. The
// first time they hit a detail page we rewrite the cookie in the new format.

const COOKIE_NAME = "ft_last_deals";
const MAX_AGE_SECONDS = 21 * 24 * 60 * 60;
const MAX_IDS = 3;
const BACKEND_URL = process.env.NEXT_PUBLIC_NEW_BACKEND_URL || "";

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : "";
}

function writeCookie(name, value, maxAgeSeconds) {
  if (typeof document === "undefined") return;
  document.cookie =
    `${name}=${encodeURIComponent(value)};` +
    `max-age=${maxAgeSeconds};path=/;samesite=lax`;
}

/**
 * Parse the cookie value into structured entries. Mirrors
 * `parseLastViewedCookie` in src/lib/last-viewed-deals.js — keep both in sync
 * when format changes.
 */
function parseExisting(raw) {
  if (!raw || typeof raw !== "string") return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];

  if (trimmed.includes("|") || trimmed.includes(":")) {
    return trimmed
      .split("|")
      .map((segment) => segment.trim())
      .filter(Boolean)
      .map((segment) => {
        const [id, seenCountRaw, lastSeenRaw] = segment.split(":");
        const cleanId = (id || "").trim();
        if (!cleanId) return null;
        const seenCount = Number(seenCountRaw);
        const lastSeen = Number(lastSeenRaw);
        return {
          id: cleanId,
          seenCount: Number.isFinite(seenCount) && seenCount > 0 ? seenCount : 1,
          lastSeen: Number.isFinite(lastSeen) && lastSeen > 0 ? lastSeen : 0,
        };
      })
      .filter(Boolean);
  }

  // Legacy comma format — promote to entries with seenCount=1.
  return trimmed
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .map((id) => ({ id, seenCount: 1, lastSeen: 0 }));
}

function serializeEntries(entries) {
  return entries
    .map((entry) => `${entry.id}:${entry.seenCount}:${entry.lastSeen}`)
    .join("|");
}

function buildBackendUrl(pathname) {
  if (!BACKEND_URL) return "";
  return `${String(BACKEND_URL).replace(/\/+$/, "")}${pathname}`;
}

function fireViewBeacon(id) {
  const url = buildBackendUrl(
    `/api/v2/deals/${encodeURIComponent(id)}/track-view`,
  );
  if (!url) return;

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const sent = navigator.sendBeacon(url);
    if (sent) return;
  }

  fetch(url, {
    method: "POST",
    keepalive: true,
  }).catch(() => {});
}

export default function RecordDealView({ dealId }) {
  useEffect(() => {
    if (dealId == null) return;
    const id = String(dealId).trim();
    if (!id) return;

    const existing = parseExisting(readCookie(COOKIE_NAME));
    const now = Math.floor(Date.now() / 1000);

    const matchedIdx = existing.findIndex((entry) => entry.id === id);
    let updated;
    if (matchedIdx >= 0) {
      const matched = existing[matchedIdx];
      updated = [
        { id, seenCount: matched.seenCount + 1, lastSeen: now },
        ...existing.filter((_, i) => i !== matchedIdx),
      ];
    } else {
      updated = [{ id, seenCount: 1, lastSeen: now }, ...existing];
    }
    updated = updated.slice(0, MAX_IDS);

    const serialized = serializeEntries(updated);
    if (serialized !== readCookie(COOKIE_NAME)) {
      writeCookie(COOKIE_NAME, serialized, MAX_AGE_SECONDS);
    }

    fireViewBeacon(id);
  }, [dealId]);

  return null;
}
